/**
 * Shared, PURE helpers for the variant engines. No React, no DOM.
 *
 * Every engine produces/consumes the generic, zone-based `GameState` from
 * `types.ts`. These helpers handle the bookkeeping that every variant shares —
 * finding zones, splitting runs off the top of a pile, and building the next
 * immutable state — so each variant module can stay focused on its own rules.
 *
 * Immutability discipline: engines NEVER mutate the incoming state, its zones,
 * or its card arrays. Every transition returns a fresh `GameState` with fresh
 * `Zone` objects and fresh `cards` arrays for the piles that changed (untouched
 * zones are shared by reference — safe, because nothing is mutated in place).
 */
import { type Card, colorOf } from './cards';
import type { GameState, Zone, ZoneKind } from './types';

/** Find a zone by id, or throw — used after a move is already validated. */
export function zone(state: GameState, id: string): Zone {
  const z = state.zones.find((zz) => zz.id === id);
  if (!z) throw new Error(`zone not found: ${id}`);
  return z;
}

/** Find a zone by id, or `undefined`. */
export function findZone(state: GameState, id: string): Zone | undefined {
  return state.zones.find((zz) => zz.id === id);
}

/** All zones of a given kind, in deal order. */
export function zonesOfKind(state: GameState, kind: ZoneKind): Zone[] {
  return state.zones.filter((z) => z.kind === kind);
}

/** Top (movable) card of a zone, or `undefined` if empty. */
export function topCard(z: Zone): Card | undefined {
  return z.cards[z.cards.length - 1];
}

/** Locate the card with `id` inside a zone; returns its index or -1. */
export function indexOfCard(z: Zone, cardId: string): number {
  return z.cards.findIndex((c) => c.id === cardId);
}

/** Find the zone (and index) that currently holds a card id. */
export function locateCard(
  state: GameState,
  cardId: string,
): { zone: Zone; index: number } | undefined {
  for (const z of state.zones) {
    const i = indexOfCard(z, cardId);
    if (i !== -1) return { zone: z, index: i };
  }
  return undefined;
}

/** True if the two suits are opposite colors (Klondike/FreeCell tableau rule). */
export function alternates(a: Card, b: Card): boolean {
  return colorOf(a.suit) !== colorOf(b.suit);
}

/**
 * Build the next state from a map of changed zones. Zones not present in
 * `changes` are carried over by reference. `moveCount` ticks; `won` is left to
 * the caller (engines set it from their own `isWon`).
 */
export function withZones(
  state: GameState,
  changes: Record<string, Zone>,
  patch?: Partial<Pick<GameState, 'won'>>,
): GameState {
  const zones = state.zones.map((z) => changes[z.id] ?? z);
  return {
    ...state,
    zones,
    moveCount: state.moveCount + 1,
    won: patch?.won ?? state.won,
  };
}

/** A shallow clone of a zone with a replacement cards array. */
export function withCards(z: Zone, cards: Card[]): Zone {
  return { ...z, cards };
}

/** Copy a card with `faceUp` forced to a value. */
export function faced(card: Card, faceUp: boolean): Card {
  return card.faceUp === faceUp ? card : { ...card, faceUp };
}
